import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, getCurrentUser } from '@/lib/supabase';
import ProductAddForm from '@/polymet/components/product-add-form';
import { useToast } from '@/hooks/use-toast';

interface Product {
  id: string;
  name: string;
  category_id: string;
  sub_category_id: string;
  product_category_id: string;
  brand: string;
  model: string;
  condition: string;
  price: number;
  description: string;
  specifications: any[];
  images: string[];
  video_url: string;
  certifications: string[];
  location: string;
  available_date: string;
  shipping_options: any;
  year: number;
  hours_used: number;
  featured: boolean;
  in_stock: boolean;
  quantity: number;
  sku: string;
  supplier_id: string;
}

export default function SupplierProductEditPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProduct() {
      if (!productId) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      try {
        // Get current user
        const user = await getCurrentUser();
        if (!user) {
          setError('You must be logged in to edit products');
          setLoading(false);
          return;
        }

        // Fetch the product
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select(`
            *,
            product_images(image_url),
            product_specifications(name, value)
          `)
          .eq('id', productId)
          .eq('supplier_id', user.id) // Ensure user owns this product
          .single();

        if (productError) {
          console.error('Error fetching product:', productError);
          setError('Failed to load product or you do not have permission to edit this product');
          setLoading(false);
          return;
        }

        if (!productData) {
          setError('Product not found');
          setLoading(false);
          return;
        }

        // Transform the data to match the form structure
        const transformedProduct: Product = {
          id: productData.id,
          name: productData.name || '',
          category_id: productData.category_id || '',
          sub_category_id: productData.subcategory_id || '',
          product_category_id: productData.product_category_id || '',
          brand: productData.brand || '',
          model: productData.model || '',
          condition: productData.condition || 'New',
          price: productData.price || 0,
          description: productData.description || '',
          specifications: productData.product_specifications || [],
          images: productData.product_images?.map((img: any) => img.image_url) || [],
          video_url: productData.video_url || '',
          certifications: productData.certifications || [],
          location: productData.location || '',
          available_date: productData.available_date || '',
          shipping_options: productData.shipping_options || {
            localPickup: true,
            domesticShipping: false,
            internationalShipping: false,
          },
          year: productData.year || new Date().getFullYear(),
          hours_used: productData.hours_used || 0,
          featured: productData.featured || false,
          in_stock: productData.in_stock ?? true,
          quantity: productData.quantity || 1,
          sku: productData.sku || '',
          supplier_id: productData.supplier_id,
        };

        setProduct(transformedProduct);
        
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-lg font-medium text-red-800">Error</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate('/supplier-dashboard?tab=products')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Edit Product</h1>
        <p className="text-gray-600 mt-2">Update your product information</p>
        <button
          onClick={() => navigate('/supplier-dashboard?tab=products')}
          className="mt-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Products
        </button>
      </div>

      {product && (
        <ProductAddForm 
          isEditing={true} 
          existingProduct={product}
          onEditSuccess={() => {
            toast({
              title: "Success",
              description: "Product updated successfully!",
            });
            navigate('/supplier-dashboard?tab=products&success=true');
          }}
        />
      )}
    </div>
  );
} 